import React, { useEffect, useState } from 'react';
import { useAbility } from '@casl/react';
import merge from 'classnames';
import { cloneDeep, filter, findIndex, sum } from 'lodash';

import { IconWarning } from '@/components/icons/IconWarning/IconWarning';
import { OrderBlock } from '@/components/modals/SubmitOrder/components/Common/OrderBlock';
import {
  getOrderItemName,
  getOrderItemPrice,
  getShippingQuantity,
  isEquipment,
  isEsProduct,
  isInventory,
} from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { AbilityContext } from '@/hooks/ability/Can';
import { OrderInventoryType } from '@/types';

import { ProductDescription } from '../../../ProductDescription';
import { QuantitySelector } from '../../../QuantitySelector';

import { calculateModulesOverage } from './helpers';
import { Props } from './types';

import styles from './GenericEquipmentSection.module.scss';

export const GenericEquipmentSection = ({ equipment }: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const orderingQuantity = equipment.orderItem.quantity;
  const shippingQuantity = getShippingQuantity(equipment);
  const equipmentPrice = getOrderItemPrice(state, equipment);
  const ability = useAbility(AbilityContext);
  const isBulkFulfillmentEnabled = ability.can('view', 'Bulk Fulfillment Program');

  const [designedQuantity, setDesignedQuantity] = useState(0);
  const [equipmentQuantity, setEquipmentQuantity] = useState(0);

  const { rooferCanIncreaseQuantity } = equipment.priceBookEntry;
  const product = equipment.proposalItem?.product;

  useEffect(() => {
    setEquipmentQuantity(orderingQuantity);
  }, [orderingQuantity]);

  useEffect(() => {
    const { quantityByDesign, quantityOfOverage } = equipment.orderItem;
    const quantity = sum([quantityByDesign, quantityOfOverage]);

    setDesignedQuantity(quantity);
  }, [equipment.orderItem]);

  const changeQuantity = (quantity: number) => {
    setEquipmentQuantity(quantity);
    const items = cloneDeep(state.visibleOrderItems) as GlobalAny;
    const itemIndex = findIndex(state.visibleOrderItems, equipment);

    if (items[itemIndex]) {
      const bundles = Math.ceil(
        quantity / items[itemIndex].priceBookEntry.product.bundleToQuantityRatio,
      );

      items[itemIndex].proposalItem.quantity = quantity;
      items[itemIndex].orderItem.quantity = quantity;
      items[itemIndex].proposalItem.numberOfBundles = bundles;
      items[itemIndex].orderItem.numberOfBundles = bundles;

      dispatch({
        type: 'UPDATE',
        payload: {
          visibleOrderItems: items,
        },
      });
    }
  };

  const selectedInventory = filter(
    state.visibleOrderItems,
    oi => isEquipment(oi) && isInventory(oi),
  );
  const isInventoryAvailable = selectedInventory.length > 0;

  const isQuantityAdjustable =
    isInventoryAvailable &&
    isBulkFulfillmentEnabled &&
    state.order.inventoryType !== OrderInventoryType['Need Inventory'] &&
    isEsProduct(product);

  const handleEquipmentQuantity = (quantity: number) => {
    switch (true) {
      case isQuantityAdjustable:
        changeQuantity(quantity);
        break;
      case quantity === 1:
        changeQuantity(designedQuantity);
        break;
      case quantity < designedQuantity:
        changeQuantity(0);
        break;
      default:
        changeQuantity(quantity);
    }
  };

  const hasOverage =
    equipment.orderItem.quantityOfOverage !== null && equipment.orderItem.quantityOfOverage > 0;

  const isChangeQuantityLocked =
    isBulkFulfillmentEnabled &&
    isEsProduct(product) &&
    (state.order.inventoryType === OrderInventoryType['Need Inventory'] ||
      state.order.inventoryType === OrderInventoryType['Full Inventory'] ||
      !isInventoryAvailable);

  useEffect(() => {
    if (isChangeQuantityLocked) {
      changeQuantity(designedQuantity);
    }

    if (isQuantityAdjustable) {
      changeQuantity(0);
    }
  }, [isChangeQuantityLocked, isQuantityAdjustable, designedQuantity]);

  const canIncrease = () => {
    if (isChangeQuantityLocked) {
      return false;
    }

    if (isQuantityAdjustable) {
      return true;
    }

    return (rooferCanIncreaseQuantity ?? false) || equipmentQuantity === 0;
  };

  const canDecrease = () => {
    if (isChangeQuantityLocked) {
      return false;
    }

    if (isQuantityAdjustable) {
      return true;
    }

    return equipmentQuantity > designedQuantity;
  };

  const shouldShowQuantitySelector = isBulkFulfillmentEnabled
    ? state.order.inventoryType !== OrderInventoryType['Need Inventory'] &&
      state.order.inventoryType !== OrderInventoryType['Full Inventory']
    : rooferCanIncreaseQuantity;

  const minQuantity = isQuantityAdjustable ? 0 : designedQuantity;

  const bundleToQuantityRatio = equipment.priceBookEntry.product.bundleToQuantityRatio ?? 0;
  const overageModules = calculateModulesOverage(equipmentQuantity, bundleToQuantityRatio);

  if (
    isBulkFulfillmentEnabled &&
    isEsProduct(product) &&
    state.order.inventoryType === OrderInventoryType['Full Inventory']
  ) {
    return null;
  }

  return (
    <OrderBlock
      color="#FAFAFA"
      divider
      hideValue={state.isPriceHidden}
      smallHeader
      title={getOrderItemName(equipment)}
      value={equipmentPrice}
    >
      <div className={styles.panel_wrapper}>
        <div className={styles.panel_title}>
          <span className={styles.shingle_panel_title}>DESIGNED QUANTITY</span>
          <span className={styles.shingle_panel_title}>TOTAL SHIPPED</span>
        </div>
        <div className={styles.panelValues}>
          <span>
            {designedQuantity}
            {isEsProduct(product) && ` Modules (${equipment.orderItem.numberOfSquares ?? 0} SQ)`}
          </span>
          <span>{shippingQuantity}</span>
        </div>
        {hasOverage && (
          <div className={styles.panel_subtitle}>
            Final quantity includes ~{equipment.orderItem.quantityOfOverage} modules in overage
          </div>
        )}
      </div>

      {shouldShowQuantitySelector && (
        <div className={merge(styles.panel_wrapper, styles.quantity_wrapper)}>
          <div className={styles.panel_title}>
            <span className={styles.shingle_panel_title}>SELECT QUANTITY</span>
            <span className={styles.quantity_selector}>
              <QuantitySelector
                canDecrease={canDecrease()}
                canIncrease={canIncrease()}
                data-testid="select-es-quantity"
                max={1000}
                min={minQuantity}
                onChange={handleEquipmentQuantity}
                showZeroQuantity={isQuantityAdjustable}
                value={equipmentQuantity}
              />
              <span>MODULES</span>
            </span>
          </div>
          <div className={styles.quantity_description}>
            <span>The purchase of solar shingles is optional</span>
            {overageModules !== 0 && (
              <span>Final quantity includes {overageModules} modules in overage</span>
            )}
          </div>
        </div>
      )}

      {isEsProduct(product) && (
        <ProductDescription description={product?.description}>
          <>
            <div className={styles.panel_title}>
              <span className={styles.shingle_panel_title}>
                COMPATIBILITY
                <IconWarning classes={{ container: styles.warning_icon }} color="#2774AD" />
              </span>
            </div>
            <div className={styles.panel_subtitle}>
              Timberline Solar <b>(TLS)</b> and Timberline Solar - 1 <b>(TLS-1)</b> are not
              cross-compatible and cannot be installed on the same roof.
            </div>
          </>
        </ProductDescription>
      )}
    </OrderBlock>
  );
};
